'use client';

import {useCallback, useEffect, useState} from 'react';
import {useCommonData} from '@udemy-v2/common-data';
import {getMonolithApiHeaders} from '@udemy-v2/api-fetch';
import {
    OneClickCheckoutContext,
    OneClickCheckoutApiResponse,
} from '@/types/one-click-checkout-context';

/**
 * Hook to fetch one-click checkout data for a specific course
 * @param courseId - The ID of the course to fetch checkout data for
 * @returns Object containing checkout data, loading state, and error state
 *
 * @example
 * const { data, isLoading, error } = useOneClickCheckout(8324);
 * if (data?.isEnabled) {
 *   // Show one-click checkout button
 * }
 */
export function useOneClickCheckoutContext(courseId: number) {
    const commonData = useCommonData();
    const [data, setData] = useState<OneClickCheckoutContext | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | undefined>(undefined);

    const isLoggedIn = !!commonData?.user?.id;

    const fetchCheckoutData = useCallback(async () => {
        if (!isLoggedIn) {
            setData(undefined);
            return;
        }

        setIsLoading(true);
        setError(undefined);

        try {
            const response = await fetch(
                `/api-2.0/course-landing-components/${courseId}/me/?components=one_click_checkout`,
                {
                    method: 'GET',
                    headers: {
                        ...getMonolithApiHeaders(),
                    },
                },
            );

            if (!response.ok) {
                throw new Error(
                    `Failed to fetch checkout data: ${response.status} ${response.statusText}`,
                );
            }

            const apiResponse: OneClickCheckoutApiResponse = await response.json();
            setData(apiResponse.one_click_checkout);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to fetch checkout data'));
            setData(undefined);
        } finally {
            setIsLoading(false);
        }
    }, [courseId, isLoggedIn]);

    useEffect(() => {
        fetchCheckoutData();
    }, [fetchCheckoutData]);

    return {
        data,
        isLoading,
        error,
    };
}
